import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import { PageHeader, FormInput, ReportChart } from '../components/finance';
import { ClipboardText, ChartBar, TrendUp, ChartLineUp, CheckCircle, XCircle } from '@phosphor-icons/react';
import './FinancialReportsDetailPage.css';

// ==================== Export Utilities ====================

// CSV Export Utility
const generateCSV = (reportType, reportData, startDate, endDate, reportDate) => {
  let csvContent = [];
  
  const getDate = (date) => new Date(date).toLocaleDateString();
  
  // Header
  const reports = {
    'trial-balance': 'Trial Balance Report',
    'balance-sheet': 'Balance Sheet Report',
    'profit-loss': 'Profit & Loss Statement',
    'cash-flow': 'Cash Flow Statement'
  };
  
  csvContent.push([reports[reportType]]);
  csvContent.push([]);
  
  if (reportType === 'trial-balance') {
    csvContent.push(['As of:', getDate(reportData.as_of_date || reportDate)]);
    csvContent.push([]);
    csvContent.push(['Account Code', 'Account Name', 'Account Type', 'Debit (GHS)', 'Credit (GHS)']);
    
    (reportData.line_items || []).forEach(account => {
      csvContent.push([
        account.account_code,
        account.account_name,
        account.account_type,
        account.debit_amount || 0,
        account.credit_amount || 0
      ]);
    });
    
    csvContent.push([]);
    csvContent.push(['Total', '', '', reportData.total_debits || 0, reportData.total_credits || 0]);
    csvContent.push([reportData.is_balanced ? 'BALANCED' : 'UNBALANCED']);
  } 
  else if (reportType === 'balance-sheet') {
    csvContent.push(['As of:', getDate(reportData.as_of_date || reportDate)]);
    csvContent.push([]);
    
    // Assets
    csvContent.push(['ASSETS']);
    csvContent.push(['Account Name', 'Amount (GHS)']);
    (reportData.assets?.items || []).forEach(asset => {
      csvContent.push([asset.account_name, asset.amount || 0]);
    });
    csvContent.push(['Total Assets', reportData.total_assets || 0]);
    csvContent.push([]);
    
    // Liabilities
    csvContent.push(['LIABILITIES']);
    csvContent.push(['Account Name', 'Amount (GHS)']);
    (reportData.liabilities?.items || []).forEach(liability => {
      csvContent.push([liability.account_name, liability.amount || 0]);
    });
    csvContent.push(['Total Liabilities', reportData.total_liabilities || 0]);
    csvContent.push([]);
    
    // Equity
    csvContent.push(['EQUITY']);
    csvContent.push(['Account Name', 'Amount (GHS)']);
    (reportData.equity?.items || []).forEach(eq => {
      csvContent.push([eq.account_name, eq.amount || 0]);
    });
    csvContent.push(['Total Equity', reportData.total_equity || 0]);
    csvContent.push([]);
    
    // Verification
    csvContent.push(['Total Liabilities + Equity', (reportData.total_liabilities || 0) + (reportData.total_equity || 0)]);
    csvContent.push([reportData.is_balanced ? 'BALANCED' : 'UNBALANCED']);
  }
  else if (reportType === 'profit-loss') {
    csvContent.push(['Period:', `${getDate(startDate)} to ${getDate(endDate)}`]);
    csvContent.push([]);
    
    // Revenue
    csvContent.push(['REVENUE']);
    csvContent.push(['Account Name', 'Amount (GHS)']);
    (reportData.revenue_section?.items || []).forEach(rev => {
      csvContent.push([rev.account_name, rev.amount || 0]);
    });
    csvContent.push(['Total Revenue', reportData.total_revenue || 0]);
    csvContent.push([]);
    
    // Operating Expenses
    csvContent.push(['OPERATING EXPENSES']);
    csvContent.push(['Account Name', 'Amount (GHS)']);
    (reportData.operating_expenses_section?.items || []).forEach(exp => {
      csvContent.push([exp.account_name, exp.amount || 0]);
    });
    csvContent.push(['Total Operating Expenses', reportData.total_operating_expenses || 0]);
    csvContent.push([]);
    
    // Other Income
    if (reportData.other_income_section?.items?.length > 0) {
      csvContent.push(['OTHER INCOME']);
      csvContent.push(['Account Name', 'Amount (GHS)']);
      (reportData.other_income_section.items || []).forEach(item => {
        csvContent.push([item.account_name, item.amount || 0]);
      });
      csvContent.push(['Total Other Income', reportData.total_other_income || 0]);
      csvContent.push([]);
    }
    
    // Other Expenses
    if (reportData.other_expenses_section?.items?.length > 0) {
      csvContent.push(['OTHER EXPENSES']);
      csvContent.push(['Account Name', 'Amount (GHS)']);
      (reportData.other_expenses_section.items || []).forEach(item => {
        csvContent.push([item.account_name, item.amount || 0]);
      });
      csvContent.push(['Total Other Expenses', reportData.total_other_expenses || 0]);
      csvContent.push([]);
    }
    
    // Net Income
    csvContent.push(['Net Income (Loss)', reportData.net_income || 0]);
  }
  else if (reportType === 'cash-flow') {
    csvContent.push(['Period:', `${getDate(startDate)} to ${getDate(endDate)}`]);
    csvContent.push([]);
    
    // Operating
    csvContent.push(['OPERATING ACTIVITIES']);
    csvContent.push(['Activity', 'Amount (GHS)']);
    const operatingItems = reportData.operating_activities?.items || [];
    (operatingItems || []).forEach(item => {
      csvContent.push([item.description || item.account_name, item.amount || 0]);
    });
    csvContent.push(['Operating Cash Flow', reportData.cash_from_operations || 0]);
    csvContent.push([]);
    
    // Investing
    csvContent.push(['INVESTING ACTIVITIES']);
    csvContent.push(['Activity', 'Amount (GHS)']);
    const investingItems = reportData.investing_activities?.items || [];
    (investingItems || []).forEach(item => {
      csvContent.push([item.description || item.account_name, item.amount || 0]);
    });
    csvContent.push(['Investing Cash Flow', reportData.cash_from_investing || 0]);
    csvContent.push([]);
    
    // Financing
    const financingItems = reportData.financing_activities?.items || [];
    if (financingItems.length > 0) {
      csvContent.push(['FINANCING ACTIVITIES']);
      csvContent.push(['Activity', 'Amount (GHS)']);
      (financingItems || []).forEach(item => {
        csvContent.push([item.description || item.account_name, item.amount || 0]);
      });
      csvContent.push(['Financing Cash Flow', reportData.cash_from_financing || 0]);
      csvContent.push([]);
    }
    
    // Summary
    csvContent.push(['Net Change in Cash', reportData.net_change_in_cash || 0]);
  }
  
  // Convert to CSV format
  return csvContent.map(row => 
    row.map(cell => {
      const cellStr = String(cell);
      return cellStr.includes(',') || cellStr.includes('"') ? `"${cellStr.replace(/"/g, '""')}"` : cellStr;
    }).join(',')
  ).join('\n');
};

// PDF Export Utility (using browser print/save)
const generatePDF = (reportType, reportData, startDate, endDate, reportDate) => {
  // Create a new window with the report
  const printWindow = window.open('', '', 'width=900,height=1200');
  
  const reportTitle = {
    'trial-balance': 'Trial Balance Report',
    'balance-sheet': 'Balance Sheet Report',
    'profit-loss': 'Profit & Loss Statement',
    'cash-flow': 'Cash Flow Statement'
  }[reportType];
  
  const getDate = (date) => new Date(date).toLocaleDateString();
  
  let htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${reportTitle}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 20px;
          color: #333;
        }
        .report-title {
          text-align: center;
          font-size: 24px;
          font-weight: bold;
          margin-bottom: 10px;
        }
        .report-date {
          text-align: center;
          font-size: 12px;
          color: #666;
          margin-bottom: 20px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        th {
          background-color: #f0f0f0;
          padding: 10px;
          text-align: left;
          border: 1px solid #ddd;
          font-weight: bold;
        }
        td {
          padding: 8px 10px;
          border: 1px solid #ddd;
        }
        tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .section-title {
          font-size: 14px;
          font-weight: bold;
          margin-top: 20px;
          margin-bottom: 10px;
          background-color: #f0f0f0;
          padding: 8px;
        }
        .amount {
          text-align: right;
        }
        .subtotal {
          font-weight: bold;
          background-color: #f0f0f0;
        }
        .total {
          font-weight: bold;
          background-color: #e0e0e0;
        }
        .verification {
          margin-top: 20px;
          padding: 10px;
          border: 1px solid #ddd;
          background-color: #f9f9f9;
        }
        @media print {
          body { margin: 0; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="report-title">${reportTitle}</div>
      <div class="report-date">Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</div>
  `;
  
  if (reportType === 'trial-balance') {
    htmlContent += `<div style="text-align: center; margin-bottom: 20px; font-size: 12px;">As of ${getDate(reportData.as_of_date || reportDate)}</div>
      <table>
        <thead>
          <tr>
            <th>Code</th>
            <th>Account Name</th>
            <th style="text-align: right;">Debit (GHS)</th>
            <th style="text-align: right;">Credit (GHS)</th>
          </tr>
        </thead>
        <tbody>`;
    
    (reportData.line_items || []).forEach(account => {
      htmlContent += `<tr>
        <td>${account.account_code}</td>
        <td>${account.account_name}</td>
        <td class="amount">${(account.debit_amount || 0).toLocaleString()}</td>
        <td class="amount">${(account.credit_amount || 0).toLocaleString()}</td>
      </tr>`;
    });
    
    htmlContent += `</tbody>
        <tfoot>
          <tr class="total">
            <td colspan="2">TOTAL</td>
            <td class="amount">${(reportData.total_debits || 0).toLocaleString()}</td>
            <td class="amount">${(reportData.total_credits || 0).toLocaleString()}</td>
          </tr>
        </tfoot>
      </table>`;
    
    htmlContent += `<div class="verification">
      <strong>Status:</strong> ${reportData.is_balanced ? '✓ BALANCED (Debits = Credits)' : '✗ UNBALANCED'}
    </div>`;
  }
  else if (reportType === 'balance-sheet') {
    htmlContent += `<div style="text-align: center; margin-bottom: 20px; font-size: 12px;">As of ${getDate(reportData.as_of_date || reportDate)}</div>`;
    
    // Assets
    htmlContent += `<div class="section-title">ASSETS</div>
      <table>
        <tbody>`;
    (reportData.assets?.items || []).forEach(asset => {
      htmlContent += `<tr><td>${asset.account_name}</td><td class="amount">${(asset.amount || 0).toLocaleString()}</td></tr>`;
    });
    htmlContent += `<tr class="subtotal"><td>Total Assets</td><td class="amount">${(reportData.total_assets || 0).toLocaleString()}</td></tr></tbody></table>`;
    
    // Liabilities
    htmlContent += `<div class="section-title">LIABILITIES</div>
      <table>
        <tbody>`;
    (reportData.liabilities?.items || []).forEach(liability => {
      htmlContent += `<tr><td>${liability.account_name}</td><td class="amount">${(liability.amount || 0).toLocaleString()}</td></tr>`;
    });
    htmlContent += `<tr class="subtotal"><td>Total Liabilities</td><td class="amount">${(reportData.total_liabilities || 0).toLocaleString()}</td></tr></tbody></table>`;
    
    // Equity
    htmlContent += `<div class="section-title">EQUITY</div>
      <table>
        <tbody>`;
    (reportData.equity?.items || []).forEach(eq => {
      htmlContent += `<tr><td>${eq.account_name}</td><td class="amount">${(eq.amount || 0).toLocaleString()}</td></tr>`;
    });
    htmlContent += `<tr class="subtotal"><td>Total Equity</td><td class="amount">${(reportData.total_equity || 0).toLocaleString()}</td></tr></tbody></table>`;
    
    htmlContent += `<div class="verification">
      <strong>Verification:</strong> Total Liabilities + Equity = ${((reportData.total_liabilities || 0) + (reportData.total_equity || 0)).toLocaleString()}<br>
      <strong>Status:</strong> ${reportData.is_balanced ? '✓ BALANCED (A = L + E)' : '✗ UNBALANCED'}
    </div>`;
  }
  else if (reportType === 'profit-loss') {
    htmlContent += `<div style="text-align: center; margin-bottom: 20px; font-size: 12px;">Period: ${getDate(startDate)} to ${getDate(endDate)}</div>`;
    
    // Revenue
    htmlContent += `<div class="section-title">REVENUE</div>
      <table>
        <tbody>`;
    (reportData.revenue_section?.items || []).forEach(rev => {
      htmlContent += `<tr><td>${rev.account_name}</td><td class="amount">${(rev.amount || 0).toLocaleString()}</td></tr>`;
    });
    htmlContent += `<tr class="subtotal"><td>Total Revenue</td><td class="amount">${(reportData.total_revenue || 0).toLocaleString()}</td></tr></tbody></table>`;
    
    // Operating Expenses
    htmlContent += `<div class="section-title">OPERATING EXPENSES</div>
      <table>
        <tbody>`;
    (reportData.operating_expenses_section?.items || []).forEach(exp => {
      htmlContent += `<tr><td>${exp.account_name}</td><td class="amount">${(exp.amount || 0).toLocaleString()}</td></tr>`;
    });
    htmlContent += `<tr class="subtotal"><td>Total Operating Expenses</td><td class="amount">${(reportData.total_operating_expenses || 0).toLocaleString()}</td></tr></tbody></table>`;
    
    // Other Income
    if (reportData.other_income_section?.items?.length > 0) {
      htmlContent += `<div class="section-title">OTHER INCOME</div>
        <table>
          <tbody>`;
      (reportData.other_income_section.items || []).forEach(item => {
        htmlContent += `<tr><td>${item.account_name}</td><td class="amount">${(item.amount || 0).toLocaleString()}</td></tr>`;
      });
      htmlContent += `<tr class="subtotal"><td>Total Other Income</td><td class="amount">${(reportData.total_other_income || 0).toLocaleString()}</td></tr></tbody></table>`;
    }
    
    // Other Expenses
    if (reportData.other_expenses_section?.items?.length > 0) {
      htmlContent += `<div class="section-title">OTHER EXPENSES</div>
        <table>
          <tbody>`;
      (reportData.other_expenses_section.items || []).forEach(item => {
        htmlContent += `<tr><td>${item.account_name}</td><td class="amount">${(item.amount || 0).toLocaleString()}</td></tr>`;
      });
      htmlContent += `<tr class="subtotal"><td>Total Other Expenses</td><td class="amount">${(reportData.total_other_expenses || 0).toLocaleString()}</td></tr></tbody></table>`;
    }
    
    // Net Income
    htmlContent += `<table><tr class="total">
      <td>NET INCOME (LOSS)</td>
      <td class="amount" style="font-size: 16px;">${(reportData.net_income || 0).toLocaleString()}</td>
    </tr></table>`;
  }
  else if (reportType === 'cash-flow') {
    htmlContent += `<div style="text-align: center; margin-bottom: 20px; font-size: 12px;">Period: ${getDate(startDate)} to ${getDate(endDate)}</div>`;
    
    // Operating
    htmlContent += `<div class="section-title">OPERATING ACTIVITIES</div>
      <table>
        <tbody>`;
    const operatingItems = reportData.operating_activities?.items || [];
    (operatingItems || []).forEach(item => {
      htmlContent += `<tr><td>${item.description || item.account_name}</td><td class="amount">${(item.amount || 0).toLocaleString()}</td></tr>`;
    });
    htmlContent += `<tr class="subtotal"><td>Operating Cash Flow</td><td class="amount">${(reportData.cash_from_operations || 0).toLocaleString()}</td></tr></tbody></table>`;
    
    // Investing
    htmlContent += `<div class="section-title">INVESTING ACTIVITIES</div>
      <table>
        <tbody>`;
    const investingItems = reportData.investing_activities?.items || [];
    (investingItems || []).forEach(item => {
      htmlContent += `<tr><td>${item.description || item.account_name}</td><td class="amount">${(item.amount || 0).toLocaleString()}</td></tr>`;
    });
    htmlContent += `<tr class="subtotal"><td>Investing Cash Flow</td><td class="amount">${(reportData.cash_from_investing || 0).toLocaleString()}</td></tr></tbody></table>`;
    
    // Financing
    const financingItems = reportData.financing_activities?.items || [];
    if (financingItems.length > 0) {
      htmlContent += `<div class="section-title">FINANCING ACTIVITIES</div>
        <table>
          <tbody>`;
      (financingItems || []).forEach(item => {
        htmlContent += `<tr><td>${item.description || item.account_name}</td><td class="amount">${(item.amount || 0).toLocaleString()}</td></tr>`;
      });
      htmlContent += `<tr class="subtotal"><td>Financing Cash Flow</td><td class="amount">${(reportData.cash_from_financing || 0).toLocaleString()}</td></tr></tbody></table>`;
    }
    
    // Summary
    htmlContent += `<table><tr class="total">
      <td>NET CHANGE IN CASH</td>
      <td class="amount" style="font-size: 16px;">${(reportData.net_change_in_cash || 0).toLocaleString()}</td>
    </tr></table>`;
  }
  
  htmlContent += `
    </body>
    </html>
  `;
  
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
};

const FinancialReportsDetailPage = ({ initialReport = 'trial-balance' }) => {
  const [selectedReport, setSelectedReport] = useState(initialReport);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  const reports = [
    {
      id: 'trial-balance',
      title: 'Trial Balance',
      description: 'List of all GL accounts with their balances',
      icon: ClipboardText,
    },
    {
      id: 'balance-sheet',
      title: 'Balance Sheet',
      description: 'Assets = Liabilities + Equity',
      icon: ChartBar,
    },
    {
      id: 'profit-loss',
      title: 'Profit & Loss',
      description: 'Revenue and Expenses for the period',
      icon: TrendUp,
    },
    {
      id: 'cash-flow',
      title: 'Cash Flow',
      description: 'Track cash movement across activities',
      icon: ChartLineUp,
    },
  ];

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        let endpoint;
        
        // Period-based reports (Profit & Loss, Cash Flow)
        if (selectedReport === 'profit-loss' || selectedReport === 'cash-flow') {
          endpoint = `/api/finance-reports/${selectedReport}?start_date=${startDate}&end_date=${endDate}`;
        } 
        // Point-in-time reports (Trial Balance, Balance Sheet)
        else {
          endpoint = `/api/finance-reports/${selectedReport}?as_of_date=${reportDate}`;
        }
        
        const data = await api.get(endpoint);
        setReportData(data);
      } catch (error) {
        toast.error(error.message || 'Failed to fetch report');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [selectedReport, reportDate, startDate, endDate]);

  const handleExportPDF = () => {
    if (!reportData) {
      toast.error('No report data to export');
      return;
    }
    try {
      generatePDF(selectedReport, reportData, startDate, endDate, reportDate);
      toast.success('Opening print dialog...');
    } catch (error) {
      toast.error('Failed to generate PDF');
      console.error(error);
    }
  };

  const handleExportCSV = () => {
    if (!reportData) {
      toast.error('No report data to export');
      return;
    }
    try {
      const csv = generateCSV(selectedReport, reportData, startDate, endDate, reportDate);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const reportNames = {
        'trial-balance': 'Trial-Balance',
        'balance-sheet': 'Balance-Sheet',
        'profit-loss': 'Profit-Loss',
        'cash-flow': 'Cash-Flow'
      };
      
      const filename = `${reportNames[selectedReport]}_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`${reportNames[selectedReport]} exported as CSV`);
    } catch (error) {
      toast.error('Failed to generate CSV');
      console.error(error);
    }
  };

  const renderReportContent = () => {
    if (loading) {
      return <div className="loading">Loading report...</div>;
    }

    if (!reportData) {
      return <div className="empty-state">No data available</div>;
    }

    switch (selectedReport) {
      case 'trial-balance':
        return <TrialBalanceReport data={reportData} />;
      case 'balance-sheet':
        return <BalanceSheetReport data={reportData} />;
      case 'profit-loss':
        return <ProfitLossReport data={reportData} />;
      case 'cash-flow':
        return <CashFlowReport data={reportData} />;
      default:
        return <div>Unknown report type</div>;
    }
  };

  const currentReport = reports.find(r => r.id === selectedReport);

  return (
    <div className="financial-reports-page">
      <PageHeader
        title="Financial Reports"
        subtitle="View comprehensive financial statements"
        actions={[
          { label: 'Export PDF', className: 'secondary', onClick: handleExportPDF },
          { label: 'Export CSV', className: 'secondary', onClick: handleExportCSV }
        ]}
      />

      <div className="reports-layout">
        <div className="reports-sidebar">
          <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: 600 }}>Available Reports</h3>
          <div className="reports-menu">
            {reports.map(report => (
              <button
                key={report.id}
                className={`menu-item ${selectedReport === report.id ? 'active' : ''}`}
                onClick={() => setSelectedReport(report.id)}
              >
                <span className="icon"><report.icon size={20} /></span>
                <div className="menu-text">
                  <strong>{report.title}</strong>
                  <small>{report.description}</small>
                </div>
              </button>
            ))}
          </div>

          <div className="report-info-box">
            <h4 style={{ margin: '0 0 12px 0', fontSize: '12px', fontWeight: 600, color: '#333' }}>Report Settings</h4>
            {selectedReport === 'profit-loss' || selectedReport === 'cash-flow' ? (
              <>
                <FormInput
                  label="Start Date"
                  name="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <FormInput
                  label="End Date"
                  name="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ marginTop: '12px' }}
                />
              </>
            ) : (
              <FormInput
                label="Report Date"
                name="reportDate"
                type="date"
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
              />
            )}
            {currentReport && (
              <div style={{ marginTop: '16px', fontSize: '12px', color: '#666' }}>
                <p style={{ margin: '4px 0' }}><strong>Report:</strong> {currentReport.title}</p>
                {selectedReport === 'profit-loss' || selectedReport === 'cash-flow' ? (
                  <>
                    <p style={{ margin: '4px 0' }}><strong>Period:</strong> {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}</p>
                  </>
                ) : (
                  <p style={{ margin: '4px 0' }}><strong>As of:</strong> {new Date(reportDate).toLocaleDateString()}</p>
                )}
                <p style={{ margin: '4px 0' }}><strong>Generated:</strong> {new Date().toLocaleDateString()}</p>
              </div>
            )}
          </div>
        </div>

        <div className="reports-content">
          {currentReport && (
            <div className="report-header">
              <h2>{currentReport.title}</h2>
              <p className="report-subtitle">As of {new Date(reportDate).toLocaleDateString()}</p>
            </div>
          )}
          {renderReportContent()}
        </div>
      </div>
    </div>
  );
};

// Trial Balance Report Component
const TrialBalanceReport = ({ data }) => {
  // Group accounts by type for better organization
  const accountsByType = {};
  (data.line_items || []).forEach(account => {
    const type = account.account_type || 'other';
    if (!accountsByType[type]) {
      accountsByType[type] = [];
    }
    accountsByType[type].push(account);
  });

  const getTypeLabel = (type) => {
    const typeLabels = {
      'asset': 'Assets (Debit Normal)',
      'liability': 'Liabilities (Credit Normal)',
      'equity': 'Equity (Credit Normal)',
      'revenue': 'Revenue (Credit Normal)',
      'expense': 'Expenses (Debit Normal)',
    };
    return typeLabels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      'asset': '#0066cc',
      'liability': '#ff6b6b',
      'equity': '#9c27b0',
      'revenue': '#4caf50',
      'expense': '#ff9800',
    };
    return colors[type] || '#666';
  };

  return (
    <div className="report-content">
      <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px', fontSize: '13px', color: '#666' }}>
        <p style={{ margin: '0 0 8px 0' }}>
          <strong>Trial Balance Overview:</strong> All GL accounts with their balances as of <strong>{new Date(data.as_of_date || new Date()).toLocaleDateString()}</strong>
        </p>
        <p style={{ margin: '0' }}>
          The debit and credit columns show the actual balance for each account. The totals must be equal to ensure all entries are properly balanced.
        </p>
      </div>

      {Object.entries(accountsByType).map(([type, accounts]) => (
        <div key={type} style={{ marginBottom: '24px' }}>
          <h4 style={{ 
            margin: '0 0 12px 0', 
            color: getTypeColor(type), 
            fontSize: '14px', 
            fontWeight: 600,
            paddingBottom: '8px',
            borderBottom: `2px solid ${getTypeColor(type)}`
          }}>
            {getTypeLabel(type)}
          </h4>
          
          <table className="report-table" style={{ marginBottom: '16px' }}>
            <thead>
              <tr>
                <th style={{ width: '15%' }}>Code</th>
                <th style={{ width: '45%' }}>Account Name</th>
                <th style={{ width: '20%', textAlign: 'right' }}>Debit (GHS)</th>
                <th style={{ width: '20%', textAlign: 'right' }}>Credit (GHS)</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account, idx) => (
                <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fafafa' : '#fff' }}>
                  <td className="code" style={{ fontWeight: 500, color: '#0066cc' }}>{account.account_code}</td>
                  <td style={{ color: '#333' }}>
                    {account.account_name}
                    <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                      Normal: {account.normal_balance === 'debit' ? 'Debit' : 'Credit'}
                    </div>
                  </td>
                  <td className="amount" style={{ textAlign: 'right', color: '#0066cc', fontWeight: 500 }}>
                    {account.debit_amount && account.debit_amount > 0 ? account.debit_amount?.toLocaleString() : '-'}
                  </td>
                  <td className="amount" style={{ textAlign: 'right', color: '#ff6b6b', fontWeight: 500 }}>
                    {account.credit_amount && account.credit_amount > 0 ? account.credit_amount?.toLocaleString() : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 600 }}>
                <td colSpan="2" style={{ paddingRight: '16px' }}>Subtotal - {getTypeLabel(type)}</td>
                <td style={{ textAlign: 'right', color: '#0066cc', paddingRight: '16px' }}>
                  {accounts.reduce((sum, a) => sum + (a.debit_amount || 0), 0).toLocaleString()}
                </td>
                <td style={{ textAlign: 'right', color: '#ff6b6b', paddingRight: '16px' }}>
                  {accounts.reduce((sum, a) => sum + (a.credit_amount || 0), 0).toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ))}

      <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f9f9f9', border: '1px solid #ddd', borderRadius: '4px' }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Trial Balance Totals</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div>
            <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px' }}>Total Debits</p>
            <p style={{ margin: '0', fontSize: '18px', fontWeight: 600, color: '#0066cc' }}>
              GHS {data.total_debits?.toLocaleString()}
            </p>
          </div>
          <div>
            <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px' }}>Total Credits</p>
            <p style={{ margin: '0', fontSize: '18px', fontWeight: 600, color: '#ff6b6b' }}>
              GHS {data.total_credits?.toLocaleString()}
            </p>
          </div>
        </div>
        
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #ddd' }}>
          {data.is_balanced ? (
            <div style={{ padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '4px', color: '#28a745', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <CheckCircle size={18} /> Trial Balance is Balanced (Debits = Credits)
            </div>
          ) : (
            <div style={{ padding: '12px', backgroundColor: '#ffebee', borderRadius: '4px', color: '#dc3545', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <XCircle size={18} /> Trial Balance is Unbalanced - Debits ({data.total_debits?.toLocaleString()}) ≠ Credits ({data.total_credits?.toLocaleString()})
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Balance Sheet Report Component
const BalanceSheetReport = ({ data }) => {
  return (
    <div className="report-content balance-sheet">
      <div className="section">
        <h3>Assets</h3>
        <table className="section-table">
          <tbody>
            {data.assets?.items && data.assets.items.map((asset, idx) => (
              <tr key={idx}>
                <td className="account-name">{asset.account_name}</td>
                <td className="amount">{asset.amount?.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="subtotal">
              <td><strong>Total Assets</strong></td>
              <td className="amount"><strong>{data.total_assets?.toLocaleString()}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="section">
        <h3>Liabilities</h3>
        <table className="section-table">
          <tbody>
            {data.liabilities?.items && data.liabilities.items.map((liability, idx) => (
              <tr key={idx}>
                <td className="account-name">{liability.account_name}</td>
                <td className="amount">{liability.amount?.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="subtotal">
              <td><strong>Total Liabilities</strong></td>
              <td className="amount"><strong>{data.total_liabilities?.toLocaleString()}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="section">
        <h3>Equity</h3>
        <table className="section-table">
          <tbody>
            {data.equity?.items && data.equity.items.map((eq, idx) => (
              <tr key={idx}>
                <td className="account-name">{eq.account_name}</td>
                <td className="amount">{eq.amount?.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="subtotal">
              <td><strong>Total Equity</strong></td>
              <td className="amount"><strong>{data.total_equity?.toLocaleString()}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="verification">
        <p><strong>Total Liabilities + Equity:</strong> GHS {(data.total_liabilities + data.total_equity)?.toLocaleString()}</p>
        {data.is_balanced ? (
          <p className="balanced"><CheckCircle size={16} /> Balance Sheet is Balanced (Assets = Liabilities + Equity)</p>
        ) : (
          <p className="unbalanced"><XCircle size={16} /> Balance Sheet is Unbalanced</p>
        )}
      </div>
    </div>
  );
};

// Profit & Loss Report Component
const ProfitLossReport = ({ data }) => {
  // Map backend structure to frontend expectations
  const revenueItems = data.revenue_section?.items || [];
  const operatingExpenseItems = data.operating_expenses_section?.items || [];
  const otherIncomeItems = data.other_income_section?.items || [];
  const otherExpenseItems = data.other_expenses_section?.items || [];
  
  // Calculate total expenses from all expense sections
  const totalExpenses = (data.total_operating_expenses || 0) + (data.total_other_expenses || 0);
  
  return (
    <div className="report-content profit-loss">
      <div className="section">
        <h3>Revenue</h3>
        <table className="section-table">
          <tbody>
            {revenueItems && revenueItems.map((rev, idx) => (
              <tr key={idx}>
                <td className="account-name">{rev.account_name}</td>
                <td className="amount">{rev.amount?.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="subtotal">
              <td><strong>Total Revenue</strong></td>
              <td className="amount"><strong>{data.total_revenue?.toLocaleString()}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="section">
        <h3>Operating Expenses</h3>
        <table className="section-table">
          <tbody>
            {operatingExpenseItems && operatingExpenseItems.map((exp, idx) => (
              <tr key={idx}>
                <td className="account-name">{exp.account_name}</td>
                <td className="amount">{exp.amount?.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="subtotal">
              <td><strong>Total Operating Expenses</strong></td>
              <td className="amount"><strong>{data.total_operating_expenses?.toLocaleString()}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {otherIncomeItems.length > 0 && (
        <div className="section">
          <h3>Other Income</h3>
          <table className="section-table">
            <tbody>
              {otherIncomeItems.map((item, idx) => (
                <tr key={idx}>
                  <td className="account-name">{item.account_name}</td>
                  <td className="amount">{item.amount?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="subtotal">
                <td><strong>Total Other Income</strong></td>
                <td className="amount"><strong>{data.total_other_income?.toLocaleString()}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {otherExpenseItems.length > 0 && (
        <div className="section">
          <h3>Other Expenses</h3>
          <table className="section-table">
            <tbody>
              {otherExpenseItems.map((item, idx) => (
                <tr key={idx}>
                  <td className="account-name">{item.account_name}</td>
                  <td className="amount">{item.amount?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="subtotal">
                <td><strong>Total Other Expenses</strong></td>
                <td className="amount"><strong>{data.total_other_expenses?.toLocaleString()}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="summary">
        <table className="summary-table">
          <tbody>
            <tr>
              <td><strong>Total Revenue</strong></td>
              <td className="amount"><strong>{data.total_revenue?.toLocaleString()}</strong></td>
            </tr>
            <tr>
              <td><strong>Total Operating Expenses</strong></td>
              <td className="amount"><strong>{data.total_operating_expenses?.toLocaleString()}</strong></td>
            </tr>
            {data.total_other_income > 0 && (
              <tr>
                <td><strong>Other Income</strong></td>
                <td className="amount"><strong>{data.total_other_income?.toLocaleString()}</strong></td>
              </tr>
            )}
            {data.total_other_expenses > 0 && (
              <tr>
                <td><strong>Other Expenses</strong></td>
                <td className="amount"><strong>{data.total_other_expenses?.toLocaleString()}</strong></td>
              </tr>
            )}
            <tr className="total-row">
              <td><strong>Net Income (Loss)</strong></td>
              <td className={`amount ${data.net_income >= 0 ? 'positive' : 'negative'}`}>
                <strong>{data.net_income?.toLocaleString()}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Cash Flow Report Component
const CashFlowReport = ({ data }) => {
  // Map backend structure to frontend expectations
  const operatingItems = data.operating_activities?.items || [];
  const investingItems = data.investing_activities?.items || [];
  const financingItems = data.financing_activities?.items || [];

  return (
    <div className="report-content cash-flow">
      <div className="section">
        <h3>Operating Activities</h3>
        <table className="section-table">
          <tbody>
            {Array.isArray(operatingItems) && operatingItems.map((item, idx) => (
              <tr key={idx}>
                <td className="activity-name">{item.description || item.account_name}</td>
                <td className="amount">{item.amount?.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="subtotal">
              <td><strong>Operating Cash Flow</strong></td>
              <td className="amount"><strong>{data.cash_from_operations?.toLocaleString()}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="section">
        <h3>Investing Activities</h3>
        <table className="section-table">
          <tbody>
            {Array.isArray(investingItems) && investingItems.map((item, idx) => (
              <tr key={idx}>
                <td className="activity-name">{item.description || item.account_name}</td>
                <td className="amount">{item.amount?.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="subtotal">
              <td><strong>Investing Cash Flow</strong></td>
              <td className="amount"><strong>{data.cash_from_investing?.toLocaleString()}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {financingItems.length > 0 && (
        <div className="section">
          <h3>Financing Activities</h3>
          <table className="section-table">
            <tbody>
              {Array.isArray(financingItems) && financingItems.map((item, idx) => (
                <tr key={idx}>
                  <td className="activity-name">{item.description || item.account_name}</td>
                  <td className="amount">{item.amount?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="subtotal">
                <td><strong>Financing Cash Flow</strong></td>
                <td className="amount"><strong>{data.cash_from_financing?.toLocaleString()}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="summary">
        <table className="summary-table">
          <tbody>
            <tr>
              <td><strong>Operating Cash Flow</strong></td>
              <td className="amount"><strong>{data.cash_from_operations?.toLocaleString()}</strong></td>
            </tr>
            <tr>
              <td><strong>Investing Cash Flow</strong></td>
              <td className="amount"><strong>{data.cash_from_investing?.toLocaleString()}</strong></td>
            </tr>
            {data.cash_from_financing !== undefined && (
              <tr>
                <td><strong>Financing Cash Flow</strong></td>
                <td className="amount"><strong>{data.cash_from_financing?.toLocaleString()}</strong></td>
              </tr>
            )}
            <tr className="total-row">
              <td><strong>Net Change in Cash</strong></td>
              <td className={`amount ${data.net_change_in_cash >= 0 ? 'positive' : 'negative'}`}>
                <strong>{data.net_change_in_cash?.toLocaleString()}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FinancialReportsDetailPage;
